/**
 * @module components/analysis/genreFitDefinitions
 * @description
 *   Copy helpers for Genre fit Axis fit labels, Trend insight tip badges,
 *   and sonic-neighbor hover blurbs (client fallback when API omits `reason`).
 *
 *   Two maps:
 *   - AXIS_FIT_LABELS — short badge text on tips (Tempo, Energy, …)
 *   - GENRE_ONLY_DEFINITIONS — tooltip blurbs for axes that are not shared with
 *     Technical Analysis ScoreGrid signalDefinitions
 *
 *   tempo / energy reuse ScoreGrid definitions so View wording matches.
 */

import { getSignalDefinition } from './signalDefinitions';

/** Short badge labels for Trend insight tips / focus UI. */
const AXIS_FIT_LABELS = {
  tempo: 'Tempo',
  energy: 'Energy',
  danceability: 'Danceability',
  brightness: 'Brightness',
  rhythm_density: 'Rhythm',
  duration_fit: 'Length',
};

/** Genre-fit-only axes (no matching ScoreGrid signal id/label). */
const GENRE_ONLY_DEFINITIONS = {
  danceability:
    'How groove-friendly and body-moving the track feels compared with the genre profile.',
  brightness:
    'Timbral brightness — how light or dark the tone sits versus the genre’s typical spectral focus.',
  rhythm_density:
    'How busy or sparse the groove is compared with the rhythmic density of this genre.',
  duration_fit:
    'How well track length sits in the typical runtime range for commercially comparable tracks in this genre.',
};

/**
 * Badge text for a tip card (e.g. "Tempo"). Returns null if axis is unknown.
 * @param {string|undefined|null} axisId
 * @returns {string|null}
 */
export function getAxisFitLabel(axisId) {
  if (!axisId) return null;
  return AXIS_FIT_LABELS[axisId] || null;
}

/**
 * Tooltip body next to an Axis fit row label.
 * @param {string|undefined|null} axisId
 * @returns {string|null}
 */
export function getAxisFitDefinition(axisId) {
  if (!axisId) return null;
  // Exact shared terms with Technical Analysis View
  if (axisId === 'tempo' || axisId === 'energy') {
    return getSignalDefinition(axisId);
  }
  return GENRE_ONLY_DEFINITIONS[axisId] || null;
}

/**
 * Hover blurb for a sonic-neighbor chip — plain language, no ranking jargon.
 * Prefers API `reason`; otherwise a short fit-based fallback.
 * @param {{ label?: string, fit?: number, reason?: string }|null|undefined} neighbor
 * @returns {string|null}
 */
export function getSonicNeighborReason(neighbor) {
  if (!neighbor) return null;
  if (typeof neighbor.reason === 'string' && neighbor.reason.trim()) {
    return neighbor.reason.trim();
  }
  const label = neighbor.label || 'this genre';
  return `Sounds close to a typical ${label} lane.`;
}
